import { ORG_ROLES, type OrgRole, type Principal } from './principal.ts';
/**
 * API token scopes. A token acts as the member who created it, within one organization, and only
 * through the scopes it carries; every request is still decided by the rules of this package
 * against that member's current memberships.
 */
export const API_TOKEN_SCOPES = [
  'projects:read',
  'projects:write',
  'pipeline:run',
  'mcp:read',
  'mcp:write',
  'org:read',
  // The fleet's workers: claim Board cards, renew their leases, complete or fail them.
  'board:work',
  /** Records assistant chat turns and their ledger (the orchestrator persona's delegated token). */
  'assistant:chat',
  /** Files Board cards and comments on them, nothing else of the design (the orchestrator persona). */
  'board:file',
  /**
   * An APP's server files a NEW-PROJECT request (the Board's new-project route) in the token's organization, in To
   * Do, and attaches files to the cards THIS token filed, nothing else (2026-11-02: an app in a cortex preview or in
   * a hosted deployment asks for a project the same way, with the cortex address and an API token from its
   * settings). The creator still needs to be able to build (org admin): the token never has more than the person
   * who made it.
   */
  'board:new-project',
  /**
   * Authors the engine configuration of a design: transformations (transform nodes, validated
   * against their kind) and processing-rule code with its test cases (saved only when the sandbox
   * passes them). Never the rest of the recipe (the engine persona).
   */
  'engine:write',
  /** Runs one guarded read-only SELECT on a governed target (the analyst persona). */
  'analytics:sql',
  /** Saves report definitions, nothing else of the design (the apps persona). */
  'reports:write',
  /**
   * Builds apps in prototype mode (ADR 0018/0019): an app's files, contract, handlers, schema and
   * fixtures, and the companion declarations it runs with (workflows, agents, models), plus
   * templates. Never the recipe (sources, elements, mappings, rules, targets: governance) nor the
   * repository's administration (promote, sync). The apps persona.
   */
  'apps:write',
  /**
   * SERVICE PRINCIPALS ONLY (ADR 0015; the api honours them only on a service token): the fleet's
   * worker routes, and the held secret of a fleet key the worker runs on.
   */
  'fleet:worker',
  'fleet:secrets',
  /**
   * SERVICE PRINCIPALS ONLY: model calls on the cortex host's LLM port (/api/llm/v1), nothing else (the
   * assistant's and the app runtime's clients; threat model T25: they held fleet:secrets for it).
   */
  'llm:use',
  /**
   * SERVICE PRINCIPALS ONLY (ADR 0030): the assistant's own identity files one audit event per MCP tool call
   * (/api/audit/mcp-tool-calls), nothing else.
   */
  'mcp:audit',
  /**
   * SERVICE PRINCIPALS ONLY (ADR 0045): the app runtime asks which project an app's key call is for
   * (/internal/v1/app-key-access), nothing else.
   */
  'app_key:access',
  /**
   * SERVICE PRINCIPALS ONLY (ADR 0046): the app runtime asks which hosts an app may call and for a project secret's
   * value for a host it is bound to (/internal/v1/apps/:project/:app/egress and /http-secret), nothing else.
   */
  'app_http:use',
  /**
   * SERVICE PRINCIPALS ONLY (ADR 0044 §4): the app runtime asks the platform to invite an email address to a
   * `signed_in` app (/internal/v1/apps/:project/:app/invitations), nothing else.
   */
  'app_invite:use',
  /**
   * SERVICE PRINCIPALS ONLY (ADR 0047): the app runtime sets, reads and removes the AI key of one company of an app
   * and its source (/internal/v1/apps/:project/:app/ai/tenants/:tenant), nothing else.
   */
  'app_ai:manage',
  /**
   * A PERSON'S OWN SIGN-IN ONLY (ADR 0030): approves and merges pull requests as the person who signed in
   * to the cortex MCP with their own tool, after the MCP asked them for that one call. Never on a `ctk_`
   * token, a service, the fleet or an assistant persona: those stay agents, which never approve or merge.
   */
  'pulls:merge',
  /**
   * A PERSON'S OWN SIGN-IN ONLY (ADR 0030, amendment 2026-11-05): reads a project's Deployments (its registered
   * instances, the apps on each, their health, versions and history) as the person who signed in, who must be one
   * the page lets in (a platform admin).
   */
  'deployments:read',
  /**
   * A PERSON'S OWN SIGN-IN ONLY (ADR 0030, amendment 2026-11-05): changes a project's Deployments (registers an
   * instance, puts an app on it, deploys a version, takes one off) as the person who signed in, after the MCP asked
   * them for that one call. Never on a `ctk_` token, a service, the fleet or an assistant persona.
   */
  'deployments:manage',
] as const;

/** Scopes that mean something only on a service token (a `ctk_` token carrying them gains nothing). */
export const SERVICE_ONLY_SCOPES: readonly ApiTokenScope[] = [
  'fleet:worker',
  'fleet:secrets',
  'llm:use',
  'mcp:audit',
  'app_key:access',
  'app_http:use',
  'app_invite:use',
  'app_ai:manage',
];

/**
 * Scopes only a person's own interactive sign-in carries (an MCP authorization-code grant and the
 * project tokens exchanged from it): a token holding one acts as that person, not as an agent.
 */
export const SIGN_IN_ONLY_SCOPES: readonly ApiTokenScope[] = [
  'pulls:merge',
  'deployments:read',
  'deployments:manage',
];

/** The scopes of a person's own sign-in on a project's Deployments (ADR 0030, amendment 2026-11-05): a read, a change. */
export const DEPLOYMENT_SCOPES = {
  read: 'deployments:read',
  manage: 'deployments:manage',
} as const satisfies Record<string, ApiTokenScope>;

/**
 * Does a token's grant act as its person? Only an OAuth sign-in of the person's own client (`client`: an MCP client
 * the person consented to) that carries a sign-in scope; never a service, and never a stored API token (which has
 * no client), whatever scopes it claims.
 */
export const tokenActsAsPerson = (grant: {
  scopes: readonly string[];
  service?: string | undefined;
  client?: string | undefined;
}) => !grant.service && !!grant.client && SIGN_IN_ONLY_SCOPES.some((s) => grant.scopes.includes(s));

/**
 * Is a route one that only a person's own sign-in may call with a token (every scope it admits is a sign-in
 * scope: approving and merging a pull request)? Only such a route treats a token that acts as its person as that
 * person; every other route treats it as any token.
 */
export const routeActsAsPerson = (routeScopes: readonly string[] | null) =>
  !!routeScopes?.length && routeScopes.every((s) => SIGN_IN_ONLY_SCOPES.includes(s as ApiTokenScope));

export type ApiTokenScope = (typeof API_TOKEN_SCOPES)[number];
export const isApiTokenScope = (s: unknown): s is ApiTokenScope =>
  API_TOKEN_SCOPES.includes(s as ApiTokenScope);

/** Every API token secret starts with this, so a verifier can tell it from an access token. */
export const API_TOKEN_PREFIX = 'ctk_';
export const isApiTokenSecret = (s: unknown): s is string =>
  typeof s === 'string' && s.startsWith(API_TOKEN_PREFIX);

/** Longest lifetime of an API token. Every token expires. */
export const API_TOKEN_MAX_LIFETIME_DAYS = 366;

/**
 * Does a token reach Board cards only through `board:new-project` (no `projects:write`)? Such a token files new-project
 * requests in To Do and changes nothing but the cards it filed itself (their files).
 */
export const filesNewProjectsOnly = (scopes: readonly string[]) =>
  scopes.includes('board:new-project') && !scopes.includes('projects:write');

export function tokenHasScope(scopes: readonly string[], needed: ApiTokenScope): boolean {
  return scopes.includes(needed);
}

/**
 * May a request made with an API token reach a route that declares `required`? A route that
 * declares no scope (`null`, the default for anything administrative) is for signed-in people
 * only and refuses every token; a route with a scope needs that exact scope on the token. Fail
 * closed: an unknown scope on either side permits nothing.
 */
export function tokenPermits(
  scopes: readonly unknown[] | null | undefined,
  required: ApiTokenScope | readonly ApiTokenScope[] | null,
): boolean {
  if (required === null) return false;
  // A route that accepts several scopes (a narrow persona scope beside the broad one) needs any of them.
  const any: readonly unknown[] = typeof required === 'string' ? [required] : required;
  return (
    Array.isArray(scopes) &&
    any.some((r) => isApiTokenScope(r) && scopes.some((s) => s === r && isApiTokenScope(s)))
  );
}

/**
 * The principal a token acts as: its creator, inside the token's organization only, and never as
 * a platform admin. Every decision is still made by the rules of this package against the
 * creator's CURRENT memberships, so a demoted or removed creator takes their tokens down with them.
 *
 * `roles` narrows it further (a TEST SESSION, Board #2566): the creator acts with only the roles a test
 * declares. A membership keeps the strongest declared role that is not stronger than its own; one that
 * holds none of them (or only weaker ones) is dropped. A test principal never has more than its creator.
 */
export function tokenPrincipal(
  creator: Principal,
  orgId: string,
  roles?: readonly OrgRole[] | null,
): Principal {
  const inOrg = creator.memberships.filter((m) => m.orgId === orgId);
  return {
    userId: creator.userId,
    email: creator.email,
    platformAdmin: false,
    memberships: roles
      ? inOrg.flatMap((m) => {
          const role = capRole(m.role, roles);
          return role ? [{ ...m, role, ...(role !== m.role ? { cappedFrom: m.role } : {}) }] : [];
        })
      : inOrg,
  };
}

/** The strongest of `roles` that is not stronger than `held` (ORG_ROLES is strongest first), or null. */
export function capRole(held: string, roles: readonly string[]): OrgRole | null {
  const at = ORG_ROLES.indexOf(held as OrgRole);
  if (at < 0) return null;
  return ORG_ROLES.slice(at).find((r) => roles.includes(r)) ?? null;
}

// ── Test sessions (Board #2566) ──────────────────────────────────────────────────────────────────────
// A `browser` test signs in to a project app as a TEST PRINCIPAL: the run's starter, with only the roles
// the test declares. The api exchanges the starter's run delegation (below) at the accounts service for a
// short-lived access token of kind test session; its scopes say what it is and which roles it keeps.

/** The token-exchange `requested_token_type` of a test session. */
export const TEST_SESSION_TOKEN_TYPE = 'urn:cortex:token-type:test-session';
/**
 * The token-exchange `subject_token_type` of a session of an organization's TEST PRINCIPAL (its slug is the
 * subject token): a `browser` test of a run a service started signs in as that, never as a person.
 */
export const TEST_PRINCIPAL_TOKEN_TYPE = 'urn:cortex:token-type:test-principal';
/** The roles a test principal may hold: never an admin. */
export const TEST_PRINCIPAL_ROLES = ['member', 'app_user'] as const satisfies readonly OrgRole[];
/** Every test session's token carries this scope (and `projects:read`: it opens the project's apps). */
export const TEST_SESSION_SCOPE = 'tests:session';
/** A test session keeps the role `<r>` when its scopes list `test-role:<r>`. */
export const TEST_ROLE_SCOPE_PREFIX = 'test-role:';

/** The scopes of a test session with `roles` (org roles only; platform admin is never a test role). */
export function testSessionScopes(roles: readonly OrgRole[]): string[] {
  return [
    TEST_SESSION_SCOPE,
    'projects:read',
    ...[...new Set(roles)].map((r) => `${TEST_ROLE_SCOPE_PREFIX}${r}`),
  ];
}

/** The roles a test session's scopes keep, or null when the scopes are not a test session's. */
export function testRolesOf(scopes: readonly string[]): OrgRole[] | null {
  if (!scopes.includes(TEST_SESSION_SCOPE)) return null;
  const roles = scopes
    .filter((s) => s.startsWith(TEST_ROLE_SCOPE_PREFIX))
    .map((s) => s.slice(TEST_ROLE_SCOPE_PREFIX.length))
    .filter((r): r is OrgRole => (ORG_ROLES as readonly string[]).includes(r));
  return roles;
}

/** Parses a scope string's test roles (`test-role:member test-role:app_user`), refusing unknown ones. */
export function parseTestRoles(scope: string): { roles: OrgRole[] } | { error: string } {
  const parts = scope.split(/\s+/).filter(Boolean);
  const roles: OrgRole[] = [];
  for (const p of parts) {
    if (!p.startsWith(TEST_ROLE_SCOPE_PREFIX))
      return { error: `'${p.slice(0, 60)}' is not a test role scope` };
    const r = p.slice(TEST_ROLE_SCOPE_PREFIX.length);
    if (!(ORG_ROLES as readonly string[]).includes(r))
      return { error: `'${r.slice(0, 40)}' is not a role (${ORG_ROLES.join(', ')})` };
    roles.push(r as OrgRole);
  }
  if (!roles.length) return { error: 'a test session names at least one role (scope test-role:<role>)' };
  return { roles: [...new Set(roles)] };
}

// ── OAuth tokens (apps/accounts) ─────────────────────────────────────────────────────────────────────
// The accounts service is the OAuth 2.1 authorization server. It issues short-lived opaque access
// tokens (stored hashed) that are bound to ONE project and carry expanded API scopes; resource
// servers (apps/api, the assistant's MCP) check each one by introspection on every request, so a
// revocation takes effect at once. Two ways to get one:
//   - an MCP client, through authorization code + PKCE, asks for `mcp:read` (read-only) or
//     `mcp:write`; the resource (the per-project MCP URL) names the project;
//   - the assistant, through token exchange for a signed-in person, asks for one persona.

/** An OAuth access token secret starts with this; a refresh token with the next one. */
export const OAUTH_ACCESS_TOKEN_PREFIX = 'coa_';
export const OAUTH_REFRESH_TOKEN_PREFIX = 'cor_';
export const isOAuthAccessToken = (s: unknown): s is string =>
  typeof s === 'string' && s.startsWith(OAUTH_ACCESS_TOKEN_PREFIX);

/**
 * The project of a PERSON-WIDE MCP sign-in (ADR 0030): the one MCP (`<public>/mcp`) is signed in to once
 * for every project the person can open. Its token reaches a project only through the assistant, which
 * exchanges it for a short-lived token bound to that project; the api accepts the person-wide token itself
 * only on routes that say so (the caller's own project list), and then with the person's memberships,
 * never as a platform admin.
 */
export const PERSON_WIDE_PROJECT = '*';

/**
 * Where cortex serves its MCP (ADR 0030): the one MCP of a person at `/mcp`, one project's at
 * `/mcp/<org>/<project>` and `/mcp/p/<project>`.
 */
export const MCP_PATH = '/mcp';
/** The per-project MCP resources of an origin: the prefix a project slug follows (`https://host/mcp/p/`). */
export const mcpProjectResourcePrefix = (origin: string) => `${origin.replace(/\/+$/, '')}${MCP_PATH}/p/`;

/**
 * How an organization lets AI tools in (ADR 0030; its org admins choose): `write` (what each person may do),
 * `read` (every AI-tool token acts read-only) or `off` (every AI-tool token is refused).
 */
export const AI_TOOL_ACCESS = ['write', 'read', 'off'] as const;
export type AiToolAccess = (typeof AI_TOOL_ACCESS)[number];
export const isAiToolAccess = (v: unknown): v is AiToolAccess => AI_TOOL_ACCESS.includes(v as AiToolAccess);
/** An organization whose admins never chose lets AI tools do what each person may do (the owner, 2026-10-07). */
export const DEFAULT_AI_TOOL_ACCESS: AiToolAccess = 'write';

/**
 * Is a token an AI tool's? Every MCP sign-in carries `mcp:read`, and so does an API token made for an AI tool
 * (CI, scripts); a service, the fleet and the assistant's persona tokens never do.
 */
export const isAiToolToken = (scopes: readonly string[]) => scopes.includes('mcp:read');

/** The service client of the cortex assistant (ADR 0015): the one identity that files MCP tool calls in the audit log. */
export const ASSISTANT_SERVICE = 'cortex-assistant';

/** How an MCP tool call ended, as its audit event says: it ran, it was refused (nothing done), or it failed. */
export const MCP_TOOL_OUTCOMES = ['ok', 'refused', 'error'] as const;
export type McpToolOutcome = (typeof MCP_TOOL_OUTCOMES)[number];

/** The scopes that read and change nothing: what an AI-tool token keeps in an organization that allows only reads. */
const READ_ONLY_SCOPES: ReadonlySet<string> = new Set(['mcp:read', 'projects:read', 'org:read']);

/** A token's scopes as a read-only token: every scope that can change something dropped. */
export const readOnlyScopes = (scopes: readonly string[]) => scopes.filter((s) => READ_ONLY_SCOPES.has(s));

/** The scopes an MCP client may ask for. `mcp:read` alone is the read-only MCP. */
export const MCP_SCOPES = ['mcp:read', 'mcp:write'] as const;
export type McpScope = (typeof MCP_SCOPES)[number];

/**
 * The scopes an MCP grant carries: what the client asked for, plus the API scopes the MCP tools
 * need. Unknown scopes are dropped; nothing asked = read-only. A read-only grant carries no scope only a person's own
 * sign-in carries (it never acts as its person), so it reaches neither merges nor Deployments.
 */
export function expandMcpScopes(requested: readonly string[]): string[] {
  const write = requested.includes('mcp:write');
  return write
    ? [
        'mcp:read',
        'mcp:write',
        'projects:read',
        'projects:write',
        'pipeline:run',
        'pulls:merge',
        'deployments:read',
        'deployments:manage',
      ]
    : ['mcp:read', 'projects:read'];
}

/**
 * The scopes of an API token made for an AI tool (CI, scripts; it carries `mcp:read` and maybe `mcp:write`): the
 * MCP scopes asked for plus the API scopes the MCP's tools need, as for a sign-in, but never a scope only a
 * person's own sign-in carries. Other scopes stay as asked.
 */
export function expandApiTokenScopes(scopes: readonly string[]): string[] {
  const mcp = scopes.filter((s) => s === 'mcp:read' || s === 'mcp:write');
  const implied = mcp.length ? expandMcpScopes(mcp) : [];
  return [...new Set([...scopes, ...implied])].filter(
    (s) => !SIGN_IN_ONLY_SCOPES.includes(s as ApiTokenScope),
  );
}

/**
 * The assistant's personas. Each is a token scope (`persona:<name>`), never a container or a URL
 * path: a delegated token carries one persona and the API scopes that persona may use.
 */
export const PERSONAS = ['orchestrator', 'governance', 'engine', 'analyst', 'apps', 'agents'] as const;
export type Persona = (typeof PERSONAS)[number];
export const isPersona = (s: unknown): s is Persona => PERSONAS.includes(s as Persona);
export const PERSONA_SCOPE_PREFIX = 'persona:';

/**
 * The personas a RUN DELEGATION (a person's grant for runs of one project, persona `agents`) may be exchanged for while
 * the run goes on: the orchestrator (an `agent` test records its evaluation in the person's assistant ledger) and the
 * analyst (an evaluation of the analyst chat persona asks its turn as it; read-only: projects:read, analytics:sql).
 * Never a persona that writes the project (governance, engine, apps).
 */
export const RUN_PERSONAS = ['orchestrator', 'analyst'] as const satisfies readonly Persona[];
export type RunPersona = (typeof RUN_PERSONAS)[number];
export const isRunPersona = (s: unknown): s is RunPersona => RUN_PERSONAS.includes(s as RunPersona);

/**
 * The API scopes of each persona (least privilege: only governance writes the recipe; the engine
 * writes transformations and rule code only, the apps persona apps, their companions and report
 * definitions only, the
 * orchestrator Board cards only).
 */
export const PERSONA_API_SCOPES: Record<Persona, readonly ApiTokenScope[]> = {
  orchestrator: ['projects:read', 'assistant:chat', 'board:file'],
  governance: ['projects:read', 'projects:write', 'pipeline:run'],
  engine: ['projects:read', 'engine:write'],
  analyst: ['projects:read', 'analytics:sql'],
  // W8: the app builder. Apps and their companions are design in the repository, but not the recipe.
  apps: ['projects:read', 'reports:write', 'apps:write'],
  // Project agents (E5.3): each agent's tools are the read operations its manifest names.
  agents: ['projects:read'],
};

/**
 * The API scopes a project agent's token may carry beside its persona's, when the agent's manifest names a tool that
 * changes the project (an SDK pack's write, the chat split of 2026-10-23): asked per agent run, never more than its
 * tools need, and the api still checks the person's own role on every call.
 */
export const AGENT_WRITE_SCOPES: readonly ApiTokenScope[] = ['projects:write'];

/**
 * The scopes of a delegated persona token: the persona scope and the persona's API scopes, and for the project agents'
 * persona the write scopes its run asked for (only AGENT_WRITE_SCOPES; anything else throws).
 */
export function personaScopes(persona: Persona, extra: readonly string[] = []): string[] {
  for (const s of extra)
    if (persona !== 'agents' || !(AGENT_WRITE_SCOPES as readonly string[]).includes(s))
      throw new Error(`persona ${persona} cannot be given ${s}`);
  return [...new Set([`${PERSONA_SCOPE_PREFIX}${persona}`, ...PERSONA_API_SCOPES[persona], ...extra])];
}

/** The persona a token's scopes name, or null. */
export function personaOf(scopes: readonly string[]): Persona | null {
  for (const s of scopes) {
    if (s.startsWith(PERSONA_SCOPE_PREFIX)) {
      const p = s.slice(PERSONA_SCOPE_PREFIX.length);
      if (isPersona(p)) return p;
    }
  }
  return null;
}
